import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';
import { corporateTheme } from '../themes/corporate';

const FinancialContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const BudgetSection = styled(motion.div)`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h3`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ChartContainer = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  height: 400px;
`;

const ChartTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const BudgetBreakdown = styled.div`
  display: grid;
  /* 6 cards: 3x2 layout for better balance */
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 1200px) {
    /* Switch to 2 columns for medium screens */
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    /* Switch to auto-fit for small screens */
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`;

const BudgetCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(5px);
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || corporateTheme.primaryGradient};
  }
`;

const BudgetAmount = styled.div`
  font-size: 2.5rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.primary};
  margin-bottom: 0.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const BudgetLabel = styled.h4`
  font-size: 1.2rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const BudgetPercentage = styled.div`
  font-size: 1.1rem;
  color: ${corporateTheme.textSecondary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const BudgetDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
  font-family: ${corporateTheme.fontFamily};
`;

const FinancialProjections = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Budget allocation data with darker colors
  const budgetData = [
    { name: 'Content Marketing', value: 5000000, color: corporateTheme.chartColors.primary, percentage: 41.7 },
    { name: 'Technology', value: 2000000, color: corporateTheme.chartColors.secondary, percentage: 16.7 },
    { name: 'Personnel', value: 1500000, color: corporateTheme.chartColors.tertiary, percentage: 12.5 },
    { name: 'Marketing & BD', value: 1500000, color: corporateTheme.chartColors.quaternary, percentage: 12.5 },
    { name: 'Working Capital', value: 1500000, color: corporateTheme.chartColors.quinary, percentage: 12.5 },
    { name: 'Operations', value: 500000, color: corporateTheme.chartColors.senary, percentage: 4.2 }
  ];

  // Revenue projections
  const revenueProjections = [
    { year: '2024', revenue: 0, expenses: 8000000, profit: -8000000 },
    { year: '2025', revenue: 2800000, expenses: 4200000, profit: -1400000 },
    { year: '2026', revenue: 8500000, expenses: 5800000, profit: 2700000 },
    { year: '2027', revenue: 15200000, expenses: 7500000, profit: 7700000 },
    { year: '2028', revenue: 24800000, expenses: 9200000, profit: 15600000 }
  ];

  // Monthly breakdown for first two years
  const monthlyBreakdown = [
    { month: 'M1-6', phase: 'Development', revenue: 0, investment: 8000000 },
    { month: 'M7-12', phase: 'Launch', revenue: 500000, investment: 2000000 },
    { month: 'M13-18', phase: 'Growth', revenue: 1800000, investment: 1500000 },
    { month: 'M19-24', phase: 'Scale', revenue: 3500000, investment: 1000000 }
  ];

  const budgetDetails = [
    {
      amount: '€5M',
      label: 'Content Marketing',
      percentage: '41.7%',
      description: 'Dedicated content team, SEO tools, digital PR, and premium hosting for high-value websites',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.primary} 0%, ${corporateTheme.primary} 100%)`
    },
    {
      amount: '€2M',
      label: 'Technology',
      percentage: '16.7%',
      description: 'Platform development, infrastructure, hosting, security, and fraud detection tools',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.secondary} 0%, ${corporateTheme.success} 100%)`
    },
    {
      amount: '€1.5M',
      label: 'Personnel',
      percentage: '12.5%',
      description: 'Core team salaries, recruitment costs, training, and management for first 18-24 months',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.tertiary} 0%, ${corporateTheme.error} 100%)`
    },
    {
      amount: '€1.5M',
      label: 'Marketing & BD',
      percentage: '12.5%',
      description: 'Digital advertising, events, PR, sales team travel, and business development activities',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quaternary} 0%, ${corporateTheme.warning} 100%)`
    },
    {
      amount: '€1.5M',
      label: 'Working Capital',
      percentage: '12.5%',
      description: 'Contingency fund, initial affiliate payouts, and operational buffer during launch phase',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quinary} 0%, ${corporateTheme.accent} 100%)`
    },
    {
      amount: '€0.5M',
      label: 'Operations',
      percentage: '4.2%',
      description: 'Office setup, legal compliance, software licenses, and general administrative expenses',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.senary} 0%, ${corporateTheme.accentLight} 100%)`
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: corporateTheme.background,
          padding: '10px',
          border: `1px solid ${corporateTheme.cardBorder}`,
          borderRadius: '8px',
          color: corporateTheme.textPrimary,
          boxShadow: corporateTheme.cardShadow,
          fontFamily: corporateTheme.fontFamily
        }}>
          <p>{`${label}: €${(payload[0].value / 1000000).toFixed(1)}M`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <FinancialContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Financial Projections
        </Title>

        <BudgetSection variants={itemVariants}>
          <SectionTitle>€12 Million Investment Allocation</SectionTitle>
          <ChartGrid>
            <ChartContainer variants={itemVariants}>
              <ChartTitle>Budget Distribution</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={budgetData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {budgetData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer variants={itemVariants}>
              <ChartTitle>5-Year Revenue Projections</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueProjections}>
                  <CartesianGrid strokeDasharray="3 3" stroke={corporateTheme.cardBorder} />
                  <XAxis dataKey="year" stroke={corporateTheme.textSecondary} fontSize={12} />
                  <YAxis stroke={corporateTheme.textSecondary} fontSize={12} tickFormatter={(value) => `€${value/1000000}M`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke={corporateTheme.chartColors.primary} 
                    strokeWidth={3}
                    dot={{ fill: corporateTheme.chartColors.primary, strokeWidth: 2, r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="profit" 
                    stroke={corporateTheme.chartColors.secondary} 
                    strokeWidth={3}
                    dot={{ fill: corporateTheme.chartColors.secondary, strokeWidth: 2, r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </ChartGrid>
        </BudgetSection>

        <BudgetSection variants={itemVariants}>
          <SectionTitle>Detailed Budget Breakdown</SectionTitle>
          <BudgetBreakdown>
            {budgetDetails.map((budget, index) => (
              <BudgetCard
                key={index}
                variants={itemVariants}
                color={budget.color}
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <BudgetAmount>{budget.amount}</BudgetAmount>
                <BudgetLabel>{budget.label}</BudgetLabel>
                <BudgetPercentage>{budget.percentage} of total investment</BudgetPercentage>
                <BudgetDescription>{budget.description}</BudgetDescription>
              </BudgetCard>
            ))}
          </BudgetBreakdown>
        </BudgetSection>

        <BudgetSection variants={itemVariants}>
          <SectionTitle>Launch Phase Timeline</SectionTitle>
          <ChartContainer variants={itemVariants} style={{ margin: '0 auto', maxWidth: '900px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke={corporateTheme.cardBorder} />
                <XAxis dataKey="month" stroke={corporateTheme.textSecondary} fontSize={12} />
                <YAxis stroke={corporateTheme.textSecondary} fontSize={12} tickFormatter={(value) => `€${value/1000000}M`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" fill={corporateTheme.chartColors.primary} radius={[4, 4, 0, 0]} />
                <Bar dataKey="investment" fill={corporateTheme.chartColors.quaternary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </BudgetSection>
      </motion.div>
    </FinancialContainer>
  );
};

export default FinancialProjections; 